import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  geocodeAddress,
  isGoogleMapsConfigured,
  loadGoogleMaps,
} from "../../utils/googleMaps";

interface LocationPickerProps {
  address: string;
  latitude?: number | null;
  longitude?: number | null;
  // Changing this to a non-empty string geocodes it and moves the pin.
  locateAddress: string;
  onChange: (latitude: number, longitude: number) => void;
}

// Roughly the centre of India, shown until a location exists.
const DEFAULT_CENTER = { lat: 22.5, lng: 79 };
const DEFAULT_ZOOM = 4;
const PIN_ZOOM = 16;

const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const isCoord = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n);

type Status = "idle" | "locating" | "notFound" | "error";

const LocationPicker: React.FC<LocationPickerProps> = ({
  address,
  latitude,
  longitude,
  locateAddress,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  // Set when an address lookup moved the pin, so the map centres on it.
  const recenterRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<Status>("idle");

  const hasPin = isCoord(latitude) && isCoord(longitude);

  // Create the map once.
  useEffect(() => {
    if (!isGoogleMapsConfigured) return;
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;

        const map = new maps.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        const marker = new maps.Marker({ map: null, draggable: true });

        const commit = (pos: { lat: () => number; lng: () => number }) =>
          onChangeRef.current(round6(pos.lat()), round6(pos.lng()));

        map.addListener("click", (e: any) => commit(e.latLng));
        marker.addListener("dragend", (e: any) => commit(e.latLng));

        mapRef.current = map;
        markerRef.current = marker;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the pin in step with the form values (geocode result, drag, click,
  // or the form being reset/loaded with an existing screen).
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!ready || !map || !marker) return;

    if (isCoord(latitude) && isCoord(longitude)) {
      const position = { lat: latitude, lng: longitude };
      marker.setPosition(position);
      marker.setMap(map);

      // Centre + zoom in one jump. Animating panTo() and then calling
      // setZoom() straight away cancels the pan, which left the pin off-screen.
      // Manual drags/clicks inside the current view are left where they are.
      const zoomedOut = (map.getZoom() ?? 0) < 12;
      const outOfView = !map.getBounds()?.contains(position);
      if (recenterRef.current || zoomedOut || outOfView) {
        map.setCenter(position);
        if (zoomedOut) map.setZoom(PIN_ZOOM);
      }
      recenterRef.current = false;
    } else {
      marker.setMap(null);
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    }
  }, [ready, latitude, longitude]);

  const locate = useCallback(async (query: string) => {
    if (!query.trim()) return;
    setStatus("locating");
    try {
      const result = await geocodeAddress(query);
      if (result) {
        recenterRef.current = true;
        onChangeRef.current(round6(result.lat), round6(result.lng));
        setStatus("idle");
      } else {
        setStatus("notFound");
      }
    } catch {
      setStatus("error");
    }
  }, []);

  // Geocode when the form signals the address field was left.
  useEffect(() => {
    if (locateAddress) locate(locateAddress);
  }, [locateAddress, locate]);

  if (!isGoogleMapsConfigured) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700">
          Screen location
        </label>
        <button
          type="button"
          onClick={() => locate(address)}
          disabled={!address.trim() || status === "locating"}
          className="text-sm text-primary hover:underline disabled:opacity-50 disabled:no-underline"
        >
          {status === "locating" ? "Locating…" : "Locate address on map"}
        </button>
      </div>

      <div
        ref={containerRef}
        className="h-64 w-full rounded-md border border-gray-300 bg-gray-100"
      />

      <p className="text-xs text-gray-500">
        {hasPin ? (
          <>
            Pin at {latitude!.toFixed(6)}, {longitude!.toFixed(6)}. Drag the
            pin or click the map to correct it.
          </>
        ) : (
          <>
            Enter the address to place the pin automatically, or click the map
            to set it by hand.
          </>
        )}
      </p>

      {status === "notFound" && (
        <p className="text-xs text-amber-600">
          Couldn't find that address on the map. Add more detail (area, city,
          pincode) or click the map to place the pin.
        </p>
      )}
      {status === "error" && (
        <p className="text-xs text-red-600">
          The map couldn't be loaded. You can still save the screen without a
          location.
        </p>
      )}
    </div>
  );
};

export default LocationPicker;
